import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { Box, Typography } from '@mui/material';
import { ChartTooltipCard, ChartTooltipRow, EmptyState, Panel } from './Panel';
import { SpreadBucket } from './metrics';
import { bodyFont, chart, formatCount, headingFont, ink, ramp } from './tokens';

interface LoyaltySpreadChartProps {
  data: SpreadBucket[];
}

/**
 * How the customer base splits across lifetime stamp bands. A donut rather than
 * bars because the question is what share of customers sit in each band, and
 * the five bands are ordinal, so a sequential ramp carries the ordering.
 */
export const LoyaltySpreadChart: React.FC<LoyaltySpreadChartProps> = ({ data }) => {
  const total = data.reduce((sum, bucket) => sum + bucket.customers, 0);
  const share = (customers: number) => (total ? (customers / total) * 100 : 0);

  const renderTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null;
    const bucket = payload[0].payload as SpreadBucket;
    return (
      <ChartTooltipCard title={bucket.label}>
        <ChartTooltipRow label='Customers' value={formatCount(bucket.customers)} />
        <ChartTooltipRow label='Share' value={`${share(bucket.customers).toFixed(1)}%`} />
      </ChartTooltipCard>
    );
  };

  return (
    <Panel
      title='Customer loyalty spread'
      subtitle={total ? `${formatCount(total)} customers all time` : 'All time'}
      hint='Every customer who has ever collected a stamp here, grouped by how many they have collected in total. Not affected by the selected period.'
    >
      {!total ? (
        <EmptyState
          height={chart.height}
          message='No customers yet. Once people start collecting, you will see how deep their stamp counts run.'
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <Box sx={{ position: 'relative', width: 190, height: chart.height, flex: 'none' }}>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={data}
                  dataKey='customers'
                  nameKey='label'
                  cx='50%'
                  cy='50%'
                  innerRadius={62}
                  outerRadius={88}
                  paddingAngle={data.filter((bucket) => bucket.customers > 0).length > 1 ? 2 : 0}
                  stroke='none'
                >
                  {data.map((bucket, index) => (
                    <Cell key={bucket.label} fill={ramp[index % ramp.length]} />
                  ))}
                </Pie>
                <Tooltip content={renderTooltip} />
              </PieChart>
            </ResponsiveContainer>

            {/* The hole earns its keep by carrying the total. */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              <Typography
                sx={{
                  fontFamily: headingFont,
                  fontWeight: 700,
                  fontSize: 26,
                  lineHeight: 1,
                  color: ink.strong
                }}
              >
                {formatCount(total)}
              </Typography>
              <Typography sx={{ fontFamily: bodyFont, fontSize: 12, color: ink.faint }}>
                customers
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}
          >
            {data.map((bucket, index) => (
              <Box
                key={bucket.label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '9px minmax(0,1fr) auto auto',
                  alignItems: 'center',
                  gap: '10px',
                  py: '7px'
                }}
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '3px',
                    backgroundColor: ramp[index % ramp.length]
                  }}
                />
                <Typography
                  noWrap
                  sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.body }}
                >
                  {bucket.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: bodyFont,
                    fontWeight: 600,
                    fontSize: 14,
                    color: ink.strong
                  }}
                >
                  {formatCount(bucket.customers)}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: bodyFont,
                    fontSize: 13,
                    color: ink.faint,
                    minWidth: 46,
                    textAlign: 'right'
                  }}
                >
                  {share(bucket.customers).toFixed(0)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Panel>
  );
};

export default LoyaltySpreadChart;
